import { ContentView } from '../../api/client'

export type ThreadNode = {
    content: ContentView
    children: ThreadNode[]
    depth: number
}

/**
 * Nest a flat thread by inReplyToStatusId -> serviceContentId. The API keeps
 * the list flat so the recursion lives in one place server-side; here we only
 * attach children. A reply whose parent is not in the list (not held, or
 * beyond the server's depth cap) attaches to the root so it is never lost. So
 * does a reply caught in a cycle (including one that points at itself): such
 * nodes are unreachable from the root, so they are flattened onto it.
 */
export function buildThread(root: ContentView, replies: ContentView[]): ThreadNode {
    const byServiceId = new Map<string, ThreadNode>()
    const rootNode: ThreadNode = { content: root, children: [], depth: 0 }
    byServiceId.set(root.serviceContentId, rootNode)

    const sorted = [...replies].sort((a, b) =>
        String(a.datePosted ?? '').localeCompare(String(b.datePosted ?? ''))
    )
    const nodes = sorted.map((content) => {
        const node: ThreadNode = { content, children: [], depth: 1 }
        byServiceId.set(content.serviceContentId, node)
        return node
    })

    for (const node of nodes) {
        const parent =
            (node.content.inReplyToStatusId && byServiceId.get(node.content.inReplyToStatusId)) ||
            rootNode
        parent.children.push(node)
    }
    // Depth comes from a walk down from the root once every link is in place, as
    // a child can sort before its parent. The walk also records what the root
    // can reach.
    const visited = new Set<ThreadNode>()
    const settle = (node: ThreadNode, depth: number) => {
        visited.add(node)
        node.depth = depth
        node.children.forEach((child) => settle(child, depth + 1))
    }
    settle(rootNode, 0)

    // Replies that point at each other (or at themselves) were attached to one
    // another, never to the root, so settle could not reach them. Drop their
    // cyclic links and hang them on the root, otherwise they vanish from the
    // drawer and a renderer walking children would never terminate.
    for (const node of nodes) {
        if (visited.has(node)) continue
        node.children = []
        node.depth = 1
        rootNode.children.push(node)
    }
    return rootNode
}
